import type { RetailerProfileEntity } from '../../../retailers/entities';
import type { TechlineCaseEntity } from '../entities';

/**
 * Remove duplicate retailer profiles by retailerNumber, keeping the first
 * occurrence of each. The API sometimes returns the same retailer more than
 * once; this normalizes the list for display.
 *
 * @param profiles - the raw retailer profiles array
 * @returns a deduplicated copy of the array
 * @example
 * ```ts
 * const unique = deduplicateRetailerProfiles(caseData.retailerProfiles);
 * ```
 */
export function deduplicateRetailerProfiles(profiles: RetailerProfileEntity[]): RetailerProfileEntity[] {
    const seen = new Set<string>();
    return profiles.filter((profile) => {
        if (seen.has(profile.retailerNumber)) {
            return false;
        }
        seen.add(profile.retailerNumber);
        return true;
    });
}

/**
 * Select the primary retailer profile from case details. Falls back through
 * four sources in priority order: unique profiles (primary), all profiles
 * (primary), first unique profile, first profile. Returns undefined if no
 * profiles exist at all.
 *
 * @param caseDetails - the case details containing retailer profiles
 * @returns the primary retailer profile, or undefined if none found
 */
export function selectPrimaryRetailer(caseDetails: TechlineCaseEntity | undefined): RetailerProfileEntity | undefined {
    return (
        caseDetails?.retailerUniqueProfiles?.find((profile) => profile.isPrimary) ??
        caseDetails?.retailerProfiles?.find((profile) => profile.isPrimary) ??
        caseDetails?.retailerUniqueProfiles?.[0] ??
        caseDetails?.retailerProfiles?.[0]
    );
}
